import ExternalLink from "@/components/ExternalLink";
import { AppDialog } from "@/components/ui/AppDialog";
import {
  BodyText,
  CaptionText,
  ContentTitle,
  EyebrowText,
} from "@/components/ui/Typography";
import { pickCoursesPageAnnouncement } from "@/lib/announcementSelectors";
import { BRAND_COLORS } from "@/lib/brandColors";
import { queryKeys } from "@/lib/queryClient";
import { resolveAppLink } from "@/lib/resolveAppLink";
import { markAnnouncementRead } from "@/services/announcements.service";
import { mixpanelService } from "@/services/mixpanel.service";
import type { UserAnnouncementRow } from "@/types/announcements";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import { useAuth } from "@/lib/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";
import { usePathname } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import { Pressable, useWindowDimensions } from "react-native";
import { Button, XStack, YStack } from "tamagui";

/** Full-bleed hero — higher ratio = shorter image band. */
const HERO_ASPECT = 16 / 9;
const MODAL_MAX_WIDTH = 520;
const CONTENT_PAD_X = 28;
const CONTENT_PAD_BOTTOM = 24;

type Props = {
  userId: string | undefined;
};

function thumbFor(row: UserAnnouncementRow): string | null {
  const t = row.thumbnail_url?.trim();
  if (t) return t;
  return row.instructor_avatar?.trim() || null;
}

/**
 * /courses interstitial: unread `show_on_courses_page` row with lowest rank.
 * Marking read (CTA or dismiss) advances to the next rank via query update.
 */
export function CoursesPageAnnouncementModal({ userId }: Props) {
  const { user } = useAuth();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const [dismissHover, setDismissHover] = useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const modalWidth = Math.min(MODAL_MAX_WIDTH, windowWidth * 0.94);
  const heroHeight = Math.round(modalWidth / HERO_ASPECT);
  const { data: rows = [] } = useAnnouncements({
    userId,
    enabled: Boolean(userId),
  });

  const announcement = useMemo(
    () => pickCoursesPageAnnouncement(rows),
    [rows],
  );

  const markRead = useCallback(
    async (row: UserAnnouncementRow) => {
      queryClient.setQueryData<UserAnnouncementRow[]>(
        queryKeys.announcements(userId),
        (prev) =>
          (prev ?? []).map((p) =>
            p.id === row.id ? { ...p, is_read: true } : p,
          ),
      );
      await markAnnouncementRead(String(row.id));
    },
    [queryClient, userId],
  );

  const onDismiss = useCallback(() => {
    if (!announcement) return;
    void markRead(announcement);
  }, [announcement, markRead]);

  const onCta = useCallback(async () => {
    if (!announcement) return;
    const href = resolveAppLink(announcement.link).href;
    await mixpanelService.trackAnnouncementClick(
      announcement.id,
      announcement.title?.trim() ?? "",
      href,
      user,
      pathname,
    );
    await mixpanelService.flush();
    await markRead(announcement);
  }, [announcement, markRead, pathname, user]);

  const dialogOpen = Boolean(announcement);
  const summary = announcement?.summary?.trim();
  const thumb = announcement ? thumbFor(announcement) : null;
  const ctaLabel = announcement?.cta_label?.trim() || "Continue";
  const { href } = resolveAppLink(announcement?.link ?? null);

  const primaryCta = (opts: { nested?: boolean }) => (
    <Button
      w="100%"
      bg={BRAND_COLORS.accent}
      color="#FFF"
      borderRadius={999}
      height={48}
      fontWeight="600"
      fontSize={15}
      letterSpacing={0.15}
      pressStyle={{ opacity: 0.9, scale: 0.985 }}
      hoverStyle={{ bg: "#D24B24" }}
      pointerEvents={opts.nested ? "none" : "auto"}
      onPress={opts.nested ? undefined : () => void onCta()}
    >
      {ctaLabel}
    </Button>
  );

  return (
    <AppDialog
      open={dialogOpen}
      instanceKey="courses-announcement"
      chrome="bare"
      showClose={false}
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
      title={announcement?.title ?? ""}
      maxWidth={MODAL_MAX_WIDTH}
    >
      {announcement ? (
        <YStack w="100%">
          {thumb ? (
            <YStack
              w="100%"
              h={heroHeight}
              position="relative"
              overflow="hidden"
              backgroundColor={BRAND_COLORS.pageBg}
            >
              <Image
                source={{ uri: thumb }}
                style={{ width: "100%", height: "100%" }}
                contentFit="cover"
                transition={180}
                accessibilityIgnoresInvertColors
              />
              <LinearGradient
                colors={[
                  "rgba(255,255,255,0)",
                  "rgba(255,255,255,0.65)",
                  "rgba(255,255,255,1)",
                ]}
                locations={[0, 0.5, 1]}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: Math.round(heroHeight * 0.32),
                }}
                pointerEvents="none"
              />
            </YStack>
          ) : (
            <YStack
              w="100%"
              h={heroHeight}
              position="relative"
              overflow="hidden"
              backgroundColor={BRAND_COLORS.pageBg}
              ai="center"
              jc="center"
            >
              <LinearGradient
                colors={["#F7F3EF", "#EFEAE4", "#FFFFFF"]}
                locations={[0, 0.55, 1]}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                }}
                pointerEvents="none"
              />
              <XStack
                w={48}
                h={4}
                borderRadius={999}
                backgroundColor={BRAND_COLORS.accent}
                opacity={0.85}
                zIndex={1}
              />
            </YStack>
          )}

          <YStack
            px={CONTENT_PAD_X}
            pt={6}
            pb={CONTENT_PAD_BOTTOM}
            gap={18}
            w="100%"
          >
            <YStack gap={6} w="100%">
              <EyebrowText
                color={BRAND_COLORS.accent}
                textTransform="uppercase"
                letterSpacing={1.15}
                size="$2"
                opacity={0.95}
              >
                Announcement
              </EyebrowText>
              <ContentTitle
                color={BRAND_COLORS.text}
                letterSpacing={-0.35}
                size="$6"
                $gtMd={{ size: "$6" }}
                lineHeight={28}
              >
                {announcement.title}
              </ContentTitle>
              {summary ? (
                <BodyText
                  color={BRAND_COLORS.muted}
                  letterSpacing={0.05}
                  lineHeight={22}
                  size="$3"
                  $gtMd={{ size: "$3" }}
                  mt={2}
                >
                  {summary}
                </BodyText>
              ) : null}
            </YStack>

            <YStack gap={2} w="100%">
              {href ? (
                <ExternalLink
                  url={href}
                  onPress={() => void onCta()}
                  deferNavigation
                >
                  {primaryCta({ nested: true })}
                </ExternalLink>
              ) : (
                primaryCta({ nested: false })
              )}
              <Pressable
                onPress={onDismiss}
                accessibilityRole="button"
                accessibilityLabel="Dismiss announcement"
                hitSlop={8}
                {...({
                  onMouseEnter: () => setDismissHover(true),
                  onMouseLeave: () => setDismissHover(false),
                } as object)}
                style={{ paddingVertical: 12 }}
              >
                <CaptionText
                  ta="center"
                  color={dismissHover ? BRAND_COLORS.text : BRAND_COLORS.muted}
                  fontWeight="500"
                  letterSpacing={0.1}
                  opacity={dismissHover ? 1 : 0.9}
                >
                  Not now
                </CaptionText>
              </Pressable>
            </YStack>
          </YStack>
        </YStack>
      ) : null}
    </AppDialog>
  );
}
